var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx } from "react/jsx-runtime";
import { Auth0Provider as Auth0UserProvider } from "@auth0/nextjs-auth0/client";
import { useRouter } from "next/router";
import React, { createContext, useEffect, useRef, useState, } from "react";
import { UserWithAppsProvider } from "./AppsProvider";
import { useAuth } from "./auth";
import { getPublicRuntimeConfig } from "./utils/runtime-config";
const UserWithoutApps = ({ errorPath, children }) => {
    const { user } = useAuth();
    const router = useRouter();
    useEffect(() => {
        if (user) {
            if (user.id !== "" &&
                Object.keys(user.appInfo).length === 0 &&
                router.query.redirected !== "true") {
                router.query.redirected = "true";
                void router.push(router);
            }
        }
    }, [errorPath, user, router]);
    return _jsx(React.Fragment, { children: children });
};
export const AuthContext = createContext({
    authEnabled: false,
    user: undefined,
    roles: [],
});
const AuthContextProvider = ({ children, }) => {
    const { user, roles } = useAuth();
    return (_jsx(AuthContext.Provider, { value: { authEnabled: true, user, roles }, children: children }));
};
export const RecipientsContext = createContext({
    users: [],
    groups: [],
    isLoaded: false,
});
export const UserProvider = ({ errorPath, showApps = true, initialSelectedAppName = "", appSwitcherLabel = "View as company", providedApps, handleParentAppChanges, callUsersEndpoint = false, children, }) => {
    const { DTN_PRODUCT_CODE } = getPublicRuntimeConfig();
    const [users, setUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [tokenExp, setTokenExp] = useState(0);
    const router = useRouter();
    // Fetch the initial session expiration once on mount so the
    // polling interval has a valid baseline to compare against.
    useEffect(() => {
        const fetchInitialSession = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const res = yield fetch(`${window.location.origin}${router.basePath}/api/auth/session`);
                const data = (yield res.json());
                if (typeof data.timestamp === "number" && data.timestamp > 0) {
                    setTokenExp(data.timestamp);
                }
            }
            catch (_a) {
                // Silently ignore — user may not be authenticated yet
            }
        });
        void fetchInitialSession();
    }, [router.basePath]);
    const useInterval = (callback, delay) => {
        const savedCallback = useRef();
        useEffect(() => {
            savedCallback.current = callback;
        }, [callback]);
        // eslint-disable-next-line consistent-return
        useEffect(() => {
            const tick = () => {
                if (savedCallback.current) {
                    savedCallback.current();
                }
            };
            if (delay !== null) {
                let id = setInterval(tick, delay);
                return () => clearInterval(id);
            }
        }, [delay]);
    };
    const updateSession = () => __awaiter(void 0, void 0, void 0, function* () {
        yield fetch(`${window.location.origin}${router.basePath}/api/auth/session`)
            // eslint-disable-next-line require-await
            .then((res) => __awaiter(void 0, void 0, void 0, function* () { return res.json(); }))
            .then((data) => {
            if (typeof data.timestamp === "number" && data.timestamp > 0) {
                setTokenExp(data.timestamp);
            }
            // If timestamp is null/0, don't update — avoids resetting to 0
            // which would cause the interval to fire again immediately
        })
            .catch((error) => {
            // eslint-disable-next-line no-console
            console.log(error);
        });
    });
    // Only poll for session refresh when we have a known valid expiration.
    // tokenExp === 0 means we haven't established a session yet — don't poll.
    useInterval(() => {
        if (tokenExp > 0 && tokenExp < Math.floor(Date.now() / 1000)) {
            void updateSession();
        }
    }, 10000);
    useEffect(() => {
        if (callUsersEndpoint && DTN_PRODUCT_CODE !== undefined) {
            // DEPRECATED: The /auth/users endpoint is no longer available
            // eslint-disable-next-line no-console
            console.warn("[DEPRECATED] callUsersEndpoint is deprecated. The /auth/users endpoint has been removed. " +
                "Please migrate to direct identity service integration.");
            // Set empty defaults and mark as loaded
            setUsers([]);
            setGroups([]);
            setIsLoaded(true);
        }
    }, [callUsersEndpoint, DTN_PRODUCT_CODE]);
    if (DTN_PRODUCT_CODE === "") {
        return (_jsx(AuthContext.Provider, { value: { authEnabled: false, user: undefined, roles: [] }, children: children }));
    }
    // Note: In nextjs-auth0 v4, Auth0Provider no longer accepts profileUrl prop
    // The profile endpoint is now at /auth/profile by default
    return (_jsx(Auth0UserProvider, { children: _jsx(UserWithoutApps, { errorPath: errorPath, children: _jsx(UserWithAppsProvider, { appSwitcherLabel: appSwitcherLabel, showApps: showApps, initialSelectedAppName: initialSelectedAppName, providedApps: providedApps, handleParentAppChanges: handleParentAppChanges, children: _jsx(AuthContextProvider, { children: _jsx(RecipientsContext.Provider, { value: { users, groups, isLoaded }, children: children }) }) }) }) }));
};
